// The only thing in this app that talks to the API.
//
// docs/coding-standards.md: the generated client is the only thing that talks
// to the API, and there is no hand-written fetch to an endpoint anywhere. The
// types come from src/api/types.ts, which is generated from the api schema on
// the live lake, so a view that changes shape breaks the build rather than
// breaking a screen quietly.
import { PostgrestClient } from "@supabase/postgrest-js";
import { API_BASE } from "../lib/config";
import type { Database } from "./types";

/* Fired when a request says the sign-in has expired. App.tsx renews on it.
   An event rather than a direct call, because this module knows about HTTP and
   nothing about Cognito, and wiring it the other way would make the api client
   import the auth module for one line. */
export const SESSION_STALE = "saleshive:session-stale";

export function apiClient(accessToken: string) {
  // The schema argument is not passed. In this version of postgrest-js the
  // second generic is client options, not the schema name, and because Database
  // declares only `api` the schema resolves to it on its own. PostgREST is
  // configured with db-schemas=api, so the URL root is that schema and there is
  // no profile header to send either.
  return new PostgrestClient<Database>(API_BASE, {
    headers: { Authorization: `Bearer ${accessToken}` },
    fetch: watchedFetch,
  });
}

/* The newest request that came back with an error status, or with no answer.
   postgrest-js keeps the HTTP status on the response and leaves it off the
   error. A reply with an error status and an empty body, which is what Caddy
   sends when it cannot reach PostgREST, reached a screen as an error with no
   message and no code (screen error 1236, 2026-10-26), and nothing said which
   request it was or what the box answered. The error report adds this line, so
   the Alerts screen names the request and its status, and Caddy's access log
   (engine deploy/Caddyfile) has the same request at the same time. The method
   and the path only, without the query string, as in remember() below. */
const FAILED_FRESH_MS = 60_000;
let lastFailed: { at: number; words: string } | null = null;
async function watchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  const base = typeof window === "undefined" ? "http://localhost/" : window.location.href;
  const request = `${init?.method ?? "GET"} ${new URL(url, base).pathname}`;
  try {
    const res = await fetch(input, init);
    if (!res.ok) lastFailed = { at: Date.now(), words: `HTTP ${res.status} from ${request}` };
    return res;
  } catch (e) {
    lastFailed = { at: Date.now(), words: `No answer to ${request}` };
    throw e;
  }
}
function failedRequest(): string | null {
  return lastFailed && Date.now() - lastFailed.at < FAILED_FRESH_MS ? lastFailed.words : null;
}

/** What went wrong, in words, for a screen to show. */
/* The last few API errors this session saw, for a feedback report.
   docs/plans/feedback-log.md: "the most useful single thing for a bug report
   and costs nothing to keep". In memory only, five at most, and the path
   without its query string, so no filter value or token goes into a report. */
export type RecentError = {
  at: string; path: string; code: string | null; message: string; request: string | null;
};
const recent: RecentError[] = [];
export function recentApiErrors(): RecentError[] {
  return [...recent];
}
function remember(error: { message?: string; code?: string }) {
  recent.push({
    at: new Date().toISOString(),
    path: typeof window === "undefined" ? "" : window.location.pathname,
    code: error.code ?? null,
    message: (error.message ?? "").slice(0, 200),
    request: failedRequest(),
  });
  if (recent.length > 5) recent.shift();
}

/* Every error a screen shows also goes to the engine, as a screen_error event
   on the Alerts screen with the screen and the person (engine migration 269),
   so it can be resolved later. James, 2026-10-20: until then such an error was
   on the screen and nowhere else. Once per screen and message in a page load,
   and never for an expired sign-in, which renews itself. App.tsx sets the
   token whenever the sign-in changes. A report that is refused is said in the
   console, and reporting it again could loop. */
let reportToken: string | null = null;
export function setErrorReportToken(token: string | null) {
  reportToken = token;
}
const reported = new Set<string>();
function report(error: { message?: string; code?: string }) {
  if (!reportToken || typeof window === "undefined") return;
  const screen = window.location.pathname;
  const message = (error.message ?? "").slice(0, 500) || "The request failed and gave no reason.";
  const key = `${screen}|${message}`;
  if (reported.has(key)) return;
  reported.add(key);
  const detail = [error.code ? `Code ${error.code}` : null, failedRequest()].filter(Boolean).join(". ");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  void (apiClient(reportToken).rpc as any)("report_screen_error", {
    p_screen: screen, p_message: message, p_detail: detail || null,
  }).then(({ error: refused }: { error: { message?: string } | null }) => {
    if (refused) console.error("The engine refused the report of a screen error:", refused.message);
  });
}

export function apiErrorMessage(error: {
  message?: string;
  code?: string;
} | null): string {
  if (!error) return "The request failed and gave no reason.";
  remember(error);

  /* An expired token and a refused account are two different facts and used to
     read as one.

     James saw a raw "JWT expired" on the Ingestion screen on 2026-09-22. That
     message is PostgREST's own and it fell through to the line at the bottom,
     because the expiry does not always arrive as PGRST301. A rep reading it
     learns nothing and has no idea the fix is to wait a moment.

     So the expiry is caught by the message as well as the code, and it asks
     for a renewal on the way past. App.tsx listens for that and calls
     currentSession(), which renews from the stored refresh token. The sentence
     tells somebody to press Refresh, which by then works. */
  const text = (error.message ?? "").toLowerCase();
  if (text.includes("jwt expired") || text.includes("jwt is expired")) {
    window.dispatchEvent(new Event(SESSION_STALE));
    return "Your sign-in expired and has been renewed. Press Refresh.";
  }
  report(error);

  if (error.code === "PGRST301" || error.code === "42501") {
    // The app renews its own sign-in twice an hour, whenever the tab comes back
    // and whenever a request reports an expiry, so an expired token is no
    // longer the likely cause here and telling somebody to sign in again would
    // send them round a loop that fixes nothing. What is left is a real
    // refusal: a revoked account, or a person in no group, which leaves
    // PostgREST no role to become.
    return "Your account was refused. It has probably been taken out of its "
      + "group. Signing out and in again will not fix it. Ask James to put it "
      + "back.";
  }
  // || and not ??, because a reply with an empty body gives an empty message,
  // and the screen showed nothing but its Try again button.
  return error.message || "The request failed and gave no reason.";
}

/* Calling a write function.
 *
 * Every one of them records the caller from the verified token, so there is no
 * identity to pass. Submission is idempotent in the engine: pressing the same
 * button twice returns the same job rather than starting a second run, so
 * nothing here has to guard against a double click.
 */
export async function callFunction<T>(
  accessToken: string,
  name: Parameters<ReturnType<typeof apiClient>["rpc"]>[0],
  args: Record<string, unknown>,
): Promise<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (apiClient(accessToken).rpc as any)(name, args);
  if (error) throw new Error(apiErrorMessage(error));
  return data as T;
}
